// ── Registration Dialog ── First-time registration overlay for Solana wallet + username
// Shows before team select on first join. Stores registration in localStorage.

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';

const REGISTRATION_KEY = 'counter_pump_registered';
const REGISTRATION_DATA_KEY = 'counter_pump_registration';

export interface RegistrationData {
  username: string;
  walletAddress: string;
}

export function isRegistered(): boolean {
  return localStorage.getItem(REGISTRATION_KEY) === 'true';
}

export function getRegistrationData(): RegistrationData | null {
  const raw = localStorage.getItem(REGISTRATION_DATA_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as RegistrationData;
  } catch {
    return null;
  }
}

export function saveRegistration(data: RegistrationData): void {
  localStorage.setItem(REGISTRATION_KEY, 'true');
  localStorage.setItem(REGISTRATION_DATA_KEY, JSON.stringify(data));
}

export class RegistrationDialog {
  private overlay: HTMLElement;
  private usernameInput: HTMLInputElement;
  private walletInput: HTMLInputElement;
  private errorEl: HTMLElement;
  private submitBtn: HTMLElement;
  private visible = false;
  private onSubmit: ((data: RegistrationData) => void) | null = null;
  private onSkip: (() => void) | null = null;
  private boundKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    const { overlay, usernameInput, walletInput, errorEl, submitBtn } = this.createOverlay();
    this.overlay = overlay;
    this.usernameInput = usernameInput;
    this.walletInput = walletInput;
    this.errorEl = errorEl;
    this.submitBtn = submitBtn;
    document.body.appendChild(this.overlay);
  }

  show(onSubmit: (data: RegistrationData) => void, onSkip: () => void): void {
    if (this.visible) return;
    this.visible = true;
    this.onSubmit = onSubmit;
    this.onSkip = onSkip;
    this.overlay.style.display = 'flex';
    this.usernameInput.value = '';
    this.walletInput.value = '';
    this.errorEl.textContent = '';
    // Focus username input after a short delay for transition
    setTimeout(() => this.usernameInput.focus(), 100);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
  }

  showError(message: string): void {
    this.errorEl.textContent = message;
  }

  dispose(): void {
    if (this.boundKeyHandler) {
      this.overlay.removeEventListener('keydown', this.boundKeyHandler);
      this.boundKeyHandler = null;
    }
    this.overlay.remove();
  }

  private validate(): string | null {
    const username = this.usernameInput.value.trim();
    const wallet = this.walletInput.value.trim();

    if (username.length < 3 || username.length > 16) {
      return 'Username must be 3-16 characters';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return 'Username: letters, numbers, underscores only';
    }
    if (wallet.length < 32 || wallet.length > 44) {
      return 'Invalid Solana wallet address';
    }
    if (!/^[1-9A-HJ-NP-Za-km-z]+$/.test(wallet)) {
      return 'Invalid Solana wallet address (bad characters)';
    }
    return null;
  }

  private handleSubmit(): void {
    // Dismiss the on-screen keyboard on mobile so any validation error or
    // post-submit UI is immediately visible (and to commit any pending IME
    // composition). Safe on desktop — blur() is a no-op if not focused.
    this.usernameInput.blur();
    this.walletInput.blur();
    const error = this.validate();
    if (error) {
      this.errorEl.textContent = error;
      return;
    }
    this.errorEl.textContent = '';
    const data: RegistrationData = {
      username: this.usernameInput.value.trim(),
      walletAddress: this.walletInput.value.trim(),
    };
    this.onSubmit?.(data);
  }

  private createOverlay(): {
    overlay: HTMLElement;
    usernameInput: HTMLInputElement;
    walletInput: HTMLInputElement;
    errorEl: HTMLElement;
    submitBtn: HTMLElement;
  } {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '56',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    // Container card
    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(420px, calc(100vw - 32px))',
      maxWidth: '420px',
      boxSizing: 'border-box',
      padding: '40px',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.95)',
      textAlign: 'center',
    });

    // Title
    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '28px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      marginBottom: '6px',
      color: ACCENT,
    });
    title.textContent = 'REGISTER';
    card.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '12px',
      color: '#666',
      letterSpacing: '2px',
      marginBottom: '30px',
      textTransform: 'uppercase',
    });
    subtitle.textContent = 'LINK YOUR SOLANA WALLET';
    card.appendChild(subtitle);

    // Username label + input
    const usernameLabel = document.createElement('div');
    Object.assign(usernameLabel.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '6px',
      textAlign: 'left',
    });
    usernameLabel.textContent = 'USERNAME';
    card.appendChild(usernameLabel);

    const usernameInput = document.createElement('input');
    usernameInput.type = 'text';
    usernameInput.placeholder = 'Enter username (3-16 chars)';
    usernameInput.maxLength = 16;
    usernameInput.autocomplete = 'username';
    usernameInput.setAttribute('autocapitalize', 'off');
    usernameInput.setAttribute('autocorrect', 'off');
    usernameInput.setAttribute('spellcheck', 'false');
    usernameInput.setAttribute('inputmode', 'text');
    Object.assign(usernameInput.style, {
      width: '100%',
      padding: '12px 16px',
      fontSize: '16px',
      fontFamily: FONT,
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid #333',
      borderRadius: '4px',
      color: '#fff',
      outline: 'none',
      marginBottom: '20px',
      boxSizing: 'border-box',
      letterSpacing: '1px',
    });
    usernameInput.addEventListener('focus', () => {
      usernameInput.style.borderColor = ACCENT;
    });
    usernameInput.addEventListener('blur', () => {
      usernameInput.style.borderColor = '#333';
    });
    card.appendChild(usernameInput);

    // Wallet label + input
    const walletLabel = document.createElement('div');
    Object.assign(walletLabel.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '6px',
      textAlign: 'left',
    });
    walletLabel.textContent = 'SOLANA WALLET ADDRESS';
    card.appendChild(walletLabel);

    const walletInput = document.createElement('input');
    walletInput.type = 'text';
    walletInput.placeholder = 'Enter your Solana wallet address';
    walletInput.maxLength = 44;
    walletInput.autocomplete = 'off';
    walletInput.setAttribute('autocapitalize', 'off');
    walletInput.setAttribute('autocorrect', 'off');
    walletInput.setAttribute('spellcheck', 'false');
    walletInput.setAttribute('inputmode', 'text');
    Object.assign(walletInput.style, {
      width: '100%',
      padding: '12px 16px',
      fontSize: '16px',
      fontFamily: FONT,
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid #333',
      borderRadius: '4px',
      color: '#fff',
      outline: 'none',
      marginBottom: '8px',
      boxSizing: 'border-box',
      letterSpacing: '0.5px',
    });
    walletInput.addEventListener('focus', () => {
      walletInput.style.borderColor = ACCENT;
    });
    walletInput.addEventListener('blur', () => {
      walletInput.style.borderColor = '#333';
    });
    card.appendChild(walletInput);

    // Wallet hint
    const walletHint = document.createElement('div');
    Object.assign(walletHint.style, {
      fontSize: '10px',
      color: '#555',
      marginBottom: '20px',
      textAlign: 'left',
    });
    walletHint.textContent = 'Your address will appear on the public leaderboard';
    card.appendChild(walletHint);

    // Error message
    const errorEl = document.createElement('div');
    Object.assign(errorEl.style, {
      fontSize: '13px',
      color: '#ff4444',
      minHeight: '20px',
      marginBottom: '16px',
      letterSpacing: '1px',
    });
    card.appendChild(errorEl);

    // Submit button
    const submitBtn = document.createElement('div');
    Object.assign(submitBtn.style, {
      padding: '14px 40px',
      fontSize: '16px',
      fontWeight: 'bold',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      border: `2px solid ${ACCENT}`,
      borderRadius: '4px',
      background: `${ACCENT}20`,
      color: ACCENT,
      transition: 'all 0.15s',
      marginBottom: '16px',
      fontFamily: FONT,
    });
    submitBtn.textContent = 'REGISTER';
    submitBtn.addEventListener('mouseenter', () => {
      submitBtn.style.background = ACCENT;
      submitBtn.style.color = '#fff';
    });
    submitBtn.addEventListener('mouseleave', () => {
      submitBtn.style.background = `${ACCENT}20`;
      submitBtn.style.color = ACCENT;
    });
    submitBtn.addEventListener('click', () => this.handleSubmit());
    card.appendChild(submitBtn);

    // Skip button — real <button> so it's keyboard/screen-reader accessible
    // and has a real touch target (>=44px) on mobile.
    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    Object.assign(skipBtn.style, {
      padding: '12px 24px',
      minHeight: '44px',
      fontSize: '12px',
      color: '#555',
      cursor: 'pointer',
      letterSpacing: '1px',
      transition: 'color 0.15s',
      background: 'transparent',
      border: 'none',
      fontFamily: FONT,
    });
    skipBtn.textContent = 'SKIP — PLAY AS GUEST';
    skipBtn.addEventListener('mouseenter', () => {
      skipBtn.style.color = '#888';
    });
    skipBtn.addEventListener('mouseleave', () => {
      skipBtn.style.color = '#555';
    });
    skipBtn.addEventListener('click', () => {
      this.hide();
      this.onSkip?.();
    });
    card.appendChild(skipBtn);

    // Enter key submits
    this.boundKeyHandler = (e: KeyboardEvent) => {
      if (!this.visible) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
        this.onSkip?.();
      }
    };
    overlay.addEventListener('keydown', this.boundKeyHandler);

    overlay.appendChild(card);

    return { overlay, usernameInput, walletInput, errorEl, submitBtn };
  }
}
